import React, { useRef, useState } from 'react';
import { Form } from 'tabler-react';
import { toBase64 } from '../../lib/helpers';

const UploadIcon = () => (
  <svg
    width="48"
    height="48"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#6c757d"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21,15 16,10 5,21" />
  </svg>
);

const ImageUpload = ({
  label,
  required = false,
  initialUrl = '',
  onChange,
  uploadText = 'Click here to upload image',
  hint = 'Any image format',
  alt = 'Preview',
}) => {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState('');
  const [hasNewImage, setHasNewImage] = useState(false);
  const [removed, setRemoved] = useState(false);
  const [localError, setLocalError] = useState('');

  const currentSrc = removed ? '' : (hasNewImage ? preview : initialUrl);

  const open = () => fileRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.includes('image')) {
      setLocalError('Please select a valid image file');
      return;
    }
    const base64 = await toBase64(file);
    setPreview(base64);
    setHasNewImage(true);
    setRemoved(false);
    setLocalError('');
    if (onChange) onChange(base64);
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setPreview('');
    setHasNewImage(false);
    setRemoved(true);
    if (fileRef.current) fileRef.current.value = '';
    if (onChange) onChange('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  };

  return (
    <Form.Group>
      {label && (
        <Form.Label>
          {label}{required ? ' *' : ''}
        </Form.Label>
      )}
      <input
        type="file"
        ref={fileRef}
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {!currentSrc ? (
        <div
          className="drop-zone"
          role="button"
          tabIndex={0}
          aria-label={uploadText}
          onClick={open}
          onKeyDown={handleKeyDown}
        >
          <div className="drop-zone-icon"><UploadIcon /></div>
          <div className="drop-zone-text">{uploadText}</div>
          <div className="drop-zone-hint">{hint}</div>
        </div>
      ) : (
        <div className="image-preview-container">
          <img
            src={currentSrc}
            alt={alt}
            className="image-preview"
            onClick={open}
          />
          <button
            type="button"
            className="remove-image-btn"
            onClick={handleRemove}
            aria-label="Remove image"
          >
            ×
          </button>
        </div>
      )}

      {localError && <div className="text-danger small mt-1">{localError}</div>}
    </Form.Group>
  );
};

export default ImageUpload;
